/**
 * Change Password Modal - Web Version
 *
 * Allows logged-in B2B FleetHub users to change their password.
 * Requires current password for reauthentication before updating.
 *
 * @module components/ChangePasswordModal
 * @platform web
 */

import { FC, useState } from "react"
/* eslint-disable no-restricted-imports */
import {
  View,
  ViewStyle,
  TextStyle,
  Modal,
  Pressable,
  TextInput,
  ActivityIndicator,
} from "react-native"
/* eslint-enable no-restricted-imports */
import { EmailAuthProvider, reauthenticateWithCredential, updatePassword } from "firebase/auth"
import { Eye, EyeOff, CheckCircle } from "lucide-react"

import { Text } from "@/components/Text"
import { auth } from "@/services/firebase/auth"
import { colors, spacing } from "@/theme"

interface ChangePasswordModalProps {
  visible: boolean
  onClose: () => void
  onPasswordChanged?: () => void
}

export const ChangePasswordModal: FC<ChangePasswordModalProps> = ({
  visible,
  onClose,
  onPasswordChanged,
}) => {
  const [currentPassword, setCurrentPassword] = useState("")
  const [newPassword, setNewPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [error, setError] = useState("")
  const [success, setSuccess] = useState(false)
  const [loading, setLoading] = useState(false)
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [cancelHovered, setCancelHovered] = useState(false)
  const [submitHovered, setSubmitHovered] = useState(false)
  const [closeHovered, setCloseHovered] = useState(false)

  const resetForm = () => {
    setCurrentPassword("")
    setNewPassword("")
    setConfirmPassword("")
    setError("")
    setSuccess(false)
    setLoading(false)
    setShowCurrentPassword(false)
    setShowNewPassword(false)
  }

  const handleClose = () => {
    if (loading) return
    resetForm()
    onClose()
  }

  const handleChangePassword = async () => {
    setError("")

    if (!currentPassword || !newPassword || !confirmPassword) {
      setError("Bitte fülle alle Felder aus.")
      return
    }

    if (newPassword.length < 8) {
      setError("Das neue Passwort muss mindestens 8 Zeichen lang sein.")
      return
    }

    if (newPassword !== confirmPassword) {
      setError("Die Passwörter stimmen nicht überein.")
      return
    }

    if (currentPassword === newPassword) {
      setError("Das neue Passwort muss sich vom aktuellen unterscheiden.")
      return
    }

    const user = auth.currentUser
    if (!user || !user.email) {
      setError("Kein angemeldeter Benutzer gefunden.")
      return
    }

    setLoading(true)

    try {
      const credential = EmailAuthProvider.credential(user.email, currentPassword)
      await reauthenticateWithCredential(user, credential)
      await updatePassword(user, newPassword)

      setSuccess(true)
      onPasswordChanged?.()
    } catch (err: any) {
      const code = err?.code || ""
      if (code === "auth/wrong-password" || code === "auth/invalid-credential") {
        setError("Das aktuelle Passwort ist falsch.")
      } else if (code === "auth/weak-password") {
        setError("Das neue Passwort ist zu schwach. Verwende mindestens 8 Zeichen.")
      } else if (code === "auth/too-many-requests") {
        setError("Zu viele Versuche. Bitte warte einen Moment.")
      } else if (code === "auth/requires-recent-login") {
        setError("Bitte melde dich erneut an und versuche es nochmal.")
      } else {
        setError("Fehler beim Ändern des Passworts. Bitte versuche es erneut.")
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal visible={visible} transparent animationType="fade">
      <Pressable style={$overlay} onPress={handleClose}>
        <Pressable style={$content} onPress={(e) => e.stopPropagation()}>
          {success ? (
            <View style={$successContainer}>
              <View style={$successIcon}>
                <CheckCircle size={32} color={colors.success} strokeWidth={2} />
              </View>
              <Text style={$title}>Passwort geändert</Text>
              <Text style={$successMessage}>Dein Passwort wurde erfolgreich aktualisiert.</Text>
              <Pressable
                onPress={handleClose}
                onHoverIn={() => setCloseHovered(true)}
                onHoverOut={() => setCloseHovered(false)}
                style={[
                  $actionButton,
                  $primaryButton,
                  { flex: 0, width: "100%" },
                  closeHovered && $primaryButtonHover,
                ]}
                accessibilityRole="button"
                accessibilityLabel="Schließen"
              >
                <Text style={$primaryButtonText}>Schließen</Text>
              </Pressable>
            </View>
          ) : (
            <>
              <Text style={$title}>Passwort ändern</Text>
              <Text style={$message}>
                Gib dein aktuelles Passwort ein und wähle ein neues Passwort.
              </Text>

              {error ? (
                <View style={$errorBox}>
                  <Text style={$errorText}>{error}</Text>
                </View>
              ) : null}

              {/* Current password */}
              <View style={$inputWrapper}>
                <TextInput
                  style={$input}
                  placeholder="Aktuelles Passwort"
                  placeholderTextColor={colors.textDim}
                  secureTextEntry={!showCurrentPassword}
                  value={currentPassword}
                  onChangeText={setCurrentPassword}
                  autoComplete="current-password"
                  editable={!loading}
                />
                <Pressable
                  style={$eyeButton}
                  onPress={() => setShowCurrentPassword((p) => !p)}
                  accessibilityLabel={
                    showCurrentPassword ? "Passwort verbergen" : "Passwort anzeigen"
                  }
                >
                  {showCurrentPassword ? (
                    <EyeOff size={18} color={colors.textDim} />
                  ) : (
                    <Eye size={18} color={colors.textDim} />
                  )}
                </Pressable>
              </View>

              {/* New password */}
              <View style={$inputWrapper}>
                <TextInput
                  style={$input}
                  placeholder="Neues Passwort (min. 8 Zeichen)"
                  placeholderTextColor={colors.textDim}
                  secureTextEntry={!showNewPassword}
                  value={newPassword}
                  onChangeText={setNewPassword}
                  autoComplete="new-password"
                  editable={!loading}
                />
                <Pressable
                  style={$eyeButton}
                  onPress={() => setShowNewPassword((p) => !p)}
                  accessibilityLabel={showNewPassword ? "Passwort verbergen" : "Passwort anzeigen"}
                >
                  {showNewPassword ? (
                    <EyeOff size={18} color={colors.textDim} />
                  ) : (
                    <Eye size={18} color={colors.textDim} />
                  )}
                </Pressable>
              </View>

              {/* Confirm password */}
              <View style={$inputWrapper}>
                <TextInput
                  style={$input}
                  placeholder="Neues Passwort bestätigen"
                  placeholderTextColor={colors.textDim}
                  secureTextEntry={!showNewPassword}
                  value={confirmPassword}
                  onChangeText={setConfirmPassword}
                  autoComplete="new-password"
                  editable={!loading}
                />
              </View>

              {/* Buttons — both use the same base style for alignment */}
              <View style={$buttonRow}>
                <Pressable
                  onPress={handleClose}
                  onHoverIn={() => setCancelHovered(true)}
                  onHoverOut={() => setCancelHovered(false)}
                  disabled={loading}
                  style={[
                    $actionButton,
                    $secondaryButton,
                    cancelHovered && $secondaryButtonHover,
                    loading && $buttonDisabled,
                  ]}
                  accessibilityRole="button"
                  accessibilityLabel="Abbrechen"
                >
                  <Text style={$secondaryButtonText}>Abbrechen</Text>
                </Pressable>
                <Pressable
                  onPress={handleChangePassword}
                  onHoverIn={() => setSubmitHovered(true)}
                  onHoverOut={() => setSubmitHovered(false)}
                  disabled={loading}
                  style={[
                    $actionButton,
                    $primaryButton,
                    submitHovered && !loading && $primaryButtonHover,
                    loading && $buttonDisabled,
                  ]}
                  accessibilityRole="button"
                  accessibilityLabel="Passwort ändern"
                >
                  {loading ? (
                    <ActivityIndicator size="small" color={colors.palette.neutral100} />
                  ) : (
                    <Text style={$primaryButtonText}>Passwort ändern</Text>
                  )}
                </Pressable>
              </View>
            </>
          )}
        </Pressable>
      </Pressable>
    </Modal>
  )
}

// Styles
const $overlay: ViewStyle = {
  flex: 1,
  backgroundColor: colors.overlay,
  justifyContent: "center",
  alignItems: "center",
  padding: spacing.xl,
}

const $content: ViewStyle = {
  backgroundColor: colors.surface,
  borderRadius: 14,
  padding: 28,
  width: "100%",
  maxWidth: 420,
  // @ts-ignore
  boxShadow: "0 12px 40px rgba(17, 30, 44, 0.25), 0 2px 8px rgba(17, 30, 44, 0.08)",
}

const $title: TextStyle = {
  fontSize: 20,
  fontWeight: "700",
  color: colors.text,
  marginBottom: 6,
}

const $message: TextStyle = {
  fontSize: 14,
  color: colors.textDim,
  marginBottom: 20,
  lineHeight: 20,
}

const $errorBox: ViewStyle = {
  backgroundColor: "rgba(244, 67, 54, 0.08)",
  borderRadius: 8,
  paddingHorizontal: 14,
  paddingVertical: 10,
  marginBottom: 12,
}

const $errorText: TextStyle = {
  fontSize: 13,
  color: colors.error,
  lineHeight: 18,
}

const $inputWrapper: ViewStyle = {
  flexDirection: "row",
  alignItems: "center",
  borderWidth: 1,
  borderColor: colors.border,
  borderRadius: 10,
  marginBottom: 10,
  backgroundColor: colors.palette.neutral200,
  // @ts-ignore
  transition: "border-color 150ms ease",
}

const $input: TextStyle = {
  flex: 1,
  paddingHorizontal: 16,
  paddingVertical: 13,
  fontSize: 15,
  color: colors.text,
  // @ts-ignore
  outlineStyle: "none",
}

const $eyeButton: ViewStyle = {
  paddingHorizontal: 14,
  paddingVertical: 13,
  // @ts-ignore
  cursor: "pointer",
}

const $buttonRow: ViewStyle = {
  flexDirection: "row",
  gap: 10,
  marginTop: 20,
}

// Shared base for both buttons — ensures identical height and alignment
const $actionButton: ViewStyle = {
  flex: 1,
  height: 46,
  borderRadius: 10,
  alignItems: "center",
  justifyContent: "center",
  // @ts-ignore
  cursor: "pointer",
  // @ts-ignore
  transition: "all 150ms ease",
}

const $secondaryButton: ViewStyle = {
  backgroundColor: colors.palette.neutral200,
  borderWidth: 1,
  borderColor: "rgba(17, 30, 44, 0.1)",
}

const $secondaryButtonHover: ViewStyle = {
  backgroundColor: colors.palette.neutral300,
}

const $secondaryButtonText: TextStyle = {
  fontSize: 15,
  fontWeight: "600",
  color: colors.palette.neutral800,
}

const $primaryButton: ViewStyle = {
  backgroundColor: colors.palette.neutral800,
}

const $primaryButtonHover: ViewStyle = {
  backgroundColor: colors.surfaceDarkHover,
}

const $primaryButtonText: TextStyle = {
  fontSize: 15,
  fontWeight: "600",
  color: colors.palette.neutral100,
}

const $buttonDisabled: ViewStyle = {
  opacity: 0.5,
  // @ts-ignore
  cursor: "not-allowed",
}

const $successContainer: ViewStyle = {
  alignItems: "center",
  paddingVertical: 8,
}

const $successIcon: ViewStyle = {
  marginBottom: 16,
}

const $successMessage: TextStyle = {
  fontSize: 14,
  color: colors.textDim,
  marginBottom: 24,
  lineHeight: 20,
  textAlign: "center",
}
